import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for frontend error monitoring
 */
export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;

  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || 'development',

    // Performance monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Session replay for debugging (only in production)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: process.env.NODE_ENV === 'production' ? 1.0 : 0,

    // Filter out noisy errors
    beforeSend(event) {
      const errorValue = event.exception?.values?.[0]?.value || '';
      const errorType = event.exception?.values?.[0]?.type || '';

      // Don't send network errors for expected failures
      if (errorValue.includes('Failed to fetch')) {
        return null;
      }
      // Don't send auth-related errors (expected during logout, etc.)
      if (errorValue.includes('auth/')) {
        return null;
      }
      // Don't send chunk load errors (handled with auto-reload)
      if (errorType === 'ChunkLoadError' || errorValue.includes('ChunkLoadError') || errorValue.includes('Loading chunk')) {
        return null;
      }
      return event;
    },

    // Add additional context
    initialScope: {
      tags: {
        component: 'frontend',
      },
    },
  });
};

/**
 * Set user context for Sentry
 */
export const setUser = (user) => {
  if (!process.env.REACT_APP_SENTRY_DSN) return;

  if (user) {
    Sentry.setUser({
      id: user.uid,
      email: user.email,
      username: user.displayName,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Capture an exception with additional context
 */
export const captureException = (error, context = {}) => {
  if (!process.env.REACT_APP_SENTRY_DSN) {
    console.error('[Error]', error);
    return;
  }

  Sentry.withScope((scope) => {
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    if (context.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(error);
  });
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (message, category = 'app', data = {}) => {
  if (!process.env.REACT_APP_SENTRY_DSN) return;

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

/**
 * Error boundary wrapper component
 */
export const ErrorBoundary = Sentry.ErrorBoundary;

export default Sentry;
